import React, { useEffect, useState, useMemo } from 'react';
import { confirmAction } from '../utils/confirm';
import { useNavigate } from 'react-router-dom';
import { fetchZones, createZone, updateZone, deleteZone, fetchDevices } from '../services/api';
import { Zone, Device } from '../types';
import { getStatusLabel, getDeviceTypeLabel, getDeviceIcon, ui } from '../styles/shared';
import PageHeader from './shared/PageHeader';

const PRESET_COLORS = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
    '#6f42c1', '#fd7e14', '#20c997', '#e83e8c', '#6c757d'
];


// Stilar från den gemensamma stilfilen
const inputStyle = ui.input;
const labelStyle = ui.label;
const btnStyle = ui.btn;

const blankForm = () => ({ name: '', description: '', color: '#007bff', kind: 'zone' as 'zone' | 'area', parent_id: 0, device_ids: [] as string[] });

const Zones: React.FC = () => {
    const navigate = useNavigate();
    const [zones, setZones] = useState<Zone[]>([]);
    const [allDevices, setAllDevices] = useState<Device[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [form, setForm] = useState(blankForm());

    const [searchZone, setSearchZone] = useState('');
    const [expandedZone, setExpandedZone] = useState<number | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const [z, d] = await Promise.all([fetchZones(), fetchDevices()]);
            setZones(z);
            setAllDevices(d);
            setError(null);
        } catch (e: any) {
            setError(e.message || 'Kunde inte hämta zonerna');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filteredZones = useMemo(() =>
        zones.filter(z => z.name.toLowerCase().includes(searchZone.toLowerCase()) ||
            (z.description || '').toLowerCase().includes(searchZone.toLowerCase())),
        [zones, searchZone]
    );
    // Områdena som rubriker med sina zoner under, sedan zoner utan område. Sökning på ett område visar hela området.
    const areas = useMemo(() => zones.filter(z => z.kind === 'area').sort((a, b) => a.name.localeCompare(b.name, 'sv')), [zones]);
    const sections = useMemo(() => {
        const q = searchZone.toLowerCase();
        const out: { area: Zone | null; zones: Zone[] }[] = [];
        for (const a of areas) {
            const kids = zones.filter(z => z.kind !== 'area' && z.parent_id === a.id);
            const areaHit = !q || a.name.toLowerCase().includes(q) || (a.description || '').toLowerCase().includes(q);
            const shown = areaHit ? kids : kids.filter(z => filteredZones.includes(z));
            if (areaHit || shown.length) out.push({ area: a, zones: shown });
        }
        const loose = filteredZones.filter(z => z.kind !== 'area' && !z.parent_id);
        if (loose.length) out.push({ area: null, zones: loose });
        return out;
    }, [areas, zones, filteredZones, searchZone]);

    const openCreate = () => {
        setEditId(null);
        setForm(blankForm());
        setShowForm(true);
    };

    const openEdit = (zone: Zone) => {
        setEditId(zone.id);
        setForm({
            name: zone.name,
            description: zone.description || '',
            color: zone.color,
            kind: zone.kind ?? 'zone',
            parent_id: zone.parent_id ?? 0,
            device_ids: zone.devices.map(d => d.ieee_address)
        });
        setShowForm(true);
        setExpandedZone(null);
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const body = { ...form, parent_id: form.kind === 'area' ? null : (form.parent_id || null), device_ids: form.kind === 'area' ? [] : form.device_ids };
            if (editId !== null) {
                await updateZone(editId, body);
            } else {
                await createZone(body);
            }
            setShowForm(false);
            await load();
        } catch (e: any) { setError(e.message); }
    };

    const handleDelete = async (id: number, name: string) => {
        if (!confirmAction(`Ta bort zonen "${name}"? Enheterna tas inte bort.`)) return;
        try {
            await deleteZone(id);
            await load();
        } catch (e: any) { setError(e.message); }
    };

    const toggleDevice = (ieee: string) => {
        setForm(prev => ({
            ...prev,
            device_ids: prev.device_ids.includes(ieee)
                ? prev.device_ids.filter(d => d !== ieee)
                : [...prev.device_ids, ieee]
        }));
    };

    const statusColor = (s: string) =>
        s === 'online' ? '#28a745' : s === 'error' ? '#dc3545' : '#aaa';

    return (
        <div>
            <PageHeader icon="🗂️" title="Zoner" description="Samla enheter som hör ihop, till exempel ett rum eller en byggnad. Zoner används i larm, egenkontroll och översikten." />


            {error && (
                <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '4px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 'bold', color: '#721c24' }}>✕</button>
                </div>
            )}

            {/* Toolbar */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'center' }}>
                <button onClick={openCreate} style={btnStyle('#28a745')}>+ Ny zon</button>
                <input
                    value={searchZone}
                    onChange={e => setSearchZone(e.target.value)}
                    placeholder="Sök zon…"
                    style={{ ...inputStyle, maxWidth: '240px' }}
                />
                <span style={{ color: '#888', fontSize: '13px', marginLeft: 'auto' }}>
                    {filteredZones.length} {filteredZones.length === 1 ? 'zon' : 'zoner'}
                </span>
            </div>

            {/* Zone Form */}
            {showForm && (
                <div style={{
                    backgroundColor: 'white', border: `2px solid ${form.color}`,
                    borderRadius: '8px', padding: '20px', marginBottom: '20px'
                }}>
                    <h3 style={{ marginTop: 0 }}>{editId ? 'Ändra zon' : 'Ny zon'}</h3>
                    <form onSubmit={submit}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                            <div>
                                <label style={labelStyle}>Zonens namn *</label>
                                <input style={inputStyle} value={form.name} required
                                    placeholder="t.ex. Vardagsrum"
                                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                            </div>
                            <div>
                                <label style={labelStyle}>Beskrivning</label>
                                <input style={inputStyle} value={form.description}
                                    placeholder="Frivillig beskrivning"
                                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                            </div>
                            <div>
                                <label style={labelStyle}>Typ</label>
                                <div style={{ display: 'flex', gap: '14px', fontSize: '14px', paddingTop: '6px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input type="radio" checked={form.kind === 'zone'} onChange={() => setForm(p => ({ ...p, kind: 'zone' }))} /> Zon
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                                        <input type="radio" checked={form.kind === 'area'} onChange={() => setForm(p => ({ ...p, kind: 'area', parent_id: 0 }))} /> Område
                                    </label>
                                </div>
                                <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                    {form.kind === 'area'
                                        ? 'Ett område samlar zoner, t.ex. "Stugbyn" med stugorna under. Enheterna sitter i zonerna.'
                                        : 'En zon har enheter och kan ligga under ett område.'}
                                </div>
                            </div>
                            {form.kind === 'zone' && (
                                <div>
                                    <label style={labelStyle}>Ligger under (område, valfritt)</label>
                                    <select style={inputStyle} value={form.parent_id} onChange={e => setForm(p => ({ ...p, parent_id: Number(e.target.value) }))}>
                                        <option value={0}>Inget område</option>
                                        {areas.filter(a => a.id !== editId).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </select>
                                </div>
                            )}
                        </div>

                        {/* Color picker */}
                        <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>Zonens färg</label>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {PRESET_COLORS.map(c => (
                                    <button type="button" key={c} onClick={() => setForm(p => ({ ...p, color: c }))}
                                        style={{
                                            width: '28px', height: '28px', borderRadius: '50%',
                                            backgroundColor: c, border: form.color === c ? '3px solid #333' : '2px solid transparent',
                                            cursor: 'pointer', outline: 'none'
                                        }} />
                                ))}
                                <input type="color" value={form.color}
                                    onChange={e => setForm(p => ({ ...p, color: e.target.value }))}
                                    style={{ width: '36px', height: '28px', padding: '0 2px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }} />
                                <span style={{ fontSize: '13px', color: '#666' }}>{form.color}</span>
                            </div>
                        </div>

                        {/* Device picker (bara zoner, ett område har inga enheter) */}
                        {form.kind === 'zone' && <div style={{ marginBottom: '16px' }}>
                            <label style={labelStyle}>
                                Enheter i zonen ({form.device_ids.length} valda)
                            </label>
                            <div style={{
                                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                                gap: '8px', maxHeight: '260px', overflowY: 'auto',
                                padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #ddd'
                            }}>
                                {allDevices.map(d => {
                                    const selected = form.device_ids.includes(d.id);
                                    return (
                                        <label key={d.id} style={{
                                            display: 'flex', alignItems: 'center', gap: '8px',
                                            padding: '8px 10px', borderRadius: '6px', cursor: 'pointer',
                                            backgroundColor: selected ? `${form.color}18` : 'white',
                                            border: `1px solid ${selected ? form.color : '#ddd'}`,
                                            transition: 'all 0.15s'
                                        }}>
                                            <input type="checkbox"
                                                checked={form.device_ids.includes(d.id)}
                                                onChange={() => toggleDevice(d.id)}
                                                style={{ accentColor: form.color }}
                                            />
                                            <span>{getDeviceIcon(d.type)}</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 'bold', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>{getDeviceTypeLabel(d.type)}</div>
                                            </div>
                                            <span style={{ color: statusColor(d.status), fontSize: '8px' }}>●</span>
                                        </label>
                                    );
                                })}
                            </div>
                        </div>}

                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button type="submit" style={btnStyle(form.color)}>{editId ? 'Spara' : form.kind === 'area' ? 'Skapa område' : 'Skapa zon'}</button>
                            <button type="button" onClick={() => setShowForm(false)} style={btnStyle('#6c757d')}>Avbryt</button>
                        </div>
                    </form>
                </div>
            )}

            {/* Zones Grid */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Hämtar…</div>
            ) : sections.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#888', backgroundColor: 'white', borderRadius: '8px', border: '1px solid #ddd' }}>
                    {searchZone ? 'Ingen zon matchar sökningen.' : 'Inga zoner än. Klicka på ”+ Ny zon” för att skapa en.'}
                </div>
            ) : sections.map(section => (
                <div key={section.area?.id ?? 'loose'} style={{ marginBottom: '24px' }}>
                    {section.area ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', marginBottom: '10px', background: 'white', border: '1px solid #ddd', borderLeft: `6px solid ${section.area.color}`, borderRadius: '8px' }}>
                            <div style={{ flex: 1 }}>
                                <span style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>Område</span>
                                <div style={{ fontWeight: 'bold', fontSize: '17px' }}>{section.area.name} <span style={{ fontSize: '13px', fontWeight: 'normal', color: '#888' }}>{section.zones.length} {section.zones.length === 1 ? 'zon' : 'zoner'}</span></div>
                                {section.area.description && <div style={{ fontSize: '13px', color: '#666' }}>{section.area.description}</div>}
                            </div>
                            <button onClick={() => openEdit(section.area!)} style={btnStyle('#ffc107', '#000')}>Ändra</button>
                            <button onClick={() => handleDelete(section.area!.id, section.area!.name)} style={btnStyle('#dc3545')}>Ta bort</button>
                        </div>
                    ) : areas.length > 0 && (
                        <div style={{ fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: 1, margin: '0 0 10px 4px' }}>Utan område</div>
                    )}
                    {section.zones.length === 0 && (
                        <div style={{ color: '#aaa', fontSize: '13px', padding: '4px 14px' }}>Inga zoner i området än. Skapa en zon och välj området under "Ligger under".</div>
                    )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                    {section.zones.map(zone => (
                        <div key={zone.id} style={{
                            backgroundColor: 'white', borderRadius: '10px',
                            border: '1px solid #ddd', borderTop: `4px solid ${zone.color}`,
                            overflow: 'hidden', transition: 'box-shadow 0.2s',
                            boxShadow: expandedZone === zone.id ? '0 4px 20px rgba(0,0,0,0.12)' : '0 1px 4px rgba(0,0,0,0.06)'
                        }}>
                            {/* Zone header */}
                            <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setExpandedZone(expandedZone === zone.id ? null : zone.id)}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{
                                            display: 'inline-block', width: '12px', height: '12px',
                                            borderRadius: '50%', backgroundColor: zone.color, flexShrink: 0
                                        }} />
                                        <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{zone.name}</span>
                                        <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#888' }}>
                                            {expandedZone === zone.id ? '▲' : '▼'}
                                        </span>
                                    </div>
                                    {zone.description && (
                                        <div style={{ fontSize: '13px', color: '#666', marginTop: '4px', marginLeft: '20px' }}>
                                            {zone.description}
                                        </div>
                                    )}
                                    <div style={{ marginTop: '8px', marginLeft: '20px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        {/* Online/offline counts */}
                                        {(() => {
                                            const online = zone.devices.filter(d => d.status === 'online').length;
                                            const total = zone.devices.length;
                                            return (
                                                <span style={{
                                                    fontSize: '12px', padding: '2px 8px', borderRadius: '10px',
                                                    backgroundColor: `${zone.color}18`, color: zone.color, fontWeight: 'bold'
                                                }}>
                                                    {total} {total === 1 ? 'enhet' : 'enheter'}{total > 0 ? ` · ${online} uppkopplade` : ''}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                                    <button onClick={() => openEdit(zone)} style={btnStyle('#ffc107', '#000')}>Ändra</button>
                                    <button onClick={() => handleDelete(zone.id, zone.name)} style={btnStyle('#dc3545')}>Ta bort</button>
                                </div>
                            </div>

                            {/* Expanded device list */}
                            {expandedZone === zone.id && (
                                <div style={{ borderTop: '1px solid #f0f0f0' }}>
                                    {zone.devices.length === 0 ? (
                                        <div style={{ padding: '14px 16px', color: '#aaa', fontSize: '13px', textAlign: 'center' }}>
                                            Inga enheter i zonen än.
                                        </div>
                                    ) : (
                                        zone.devices.map(d => (
                                            <div key={d.id} style={{
                                                display: 'flex', alignItems: 'center', gap: '10px',
                                                padding: '10px 16px', borderBottom: '1px solid #f8f8f8',
                                                cursor: 'pointer'
                                            }}
                                                onClick={() => navigate(`/device-control?deviceId=${d.ieee_address}`)}
                                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                                                <span style={{ fontSize: '18px' }}>{getDeviceIcon(d.type)}</span>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{d.name}</div>
                                                    <div style={{ fontSize: '12px', color: '#888' }}>{getDeviceTypeLabel(d.type)}</div>
                                                </div>
                                                <span style={{ color: statusColor(d.status), fontWeight: 'bold', fontSize: '12px' }}>
                                                    ● {getStatusLabel(d.status)}
                                                </span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                </div>
            ))}
        </div>
    );
};

export default Zones;
